// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Children,
} from 'common.types';
import type {
  $Model,
} from 'common.data';

// Global Enums
import {
  AccessTokenKey,
} from 'common.types';

// Global Components
import * as React from 'react';
import {
  Loader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getUrlGetParams,
} from '@vroskus/library-helpers/dist/browser';

// Components
/* eslint-disable-next-line import/no-named-as-default */
import Base from '../../../theme/components/Layout/Base';
import Footer from '../Footer';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $CleanAuthenticationAction,
  $SetupAuthenticationAction,
} from '../../../redux/modules/auth/actions';
import type {
  $GetRegionsAction,
  $RedirectAction,
  $SetupLanguageAction,
  $SetupMainDataAction,
  $SetupUserAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $WithRouter & {
  children: $Children;
};

export type $StateProps = {
  auth: $ReducerState<'auth'>;
};

export type $DispatchProps = {
  cleanAuthentication: $CleanAuthenticationAction;
  getRegions: $GetRegionsAction;
  redirect: $RedirectAction;
  setupAuthentication: $SetupAuthenticationAction;
  setupLanguage: $SetupLanguageAction;
  setupMainData: $SetupMainDataAction;
  setupUser: $SetupUserAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  initialised: boolean;
};

class MainView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      initialised: false,
    };
  }

  componentDidMount(): void {
    this.init();
  }

  componentDidCatch(error: Error): void {
    ErrorLog.exception(error as Error);
  }

  async setupAuthData(): Promise<$Model<'Admin'> | null> {
    const {
      cleanAuthentication,
      location,
      redirect,
      setupAuthentication,
      setupUser,
    } = this.props;

    try {
      Services.Api.setUnauthenticatedHandler({
        handler: async () => {
          const pathname = '/login';

          if (location.pathname !== pathname) {
            await cleanAuthentication();

            await redirect({
              pathname,
              state: {
                from: location,
              },
            });
          }
        },
      });

      const accessToken: string | void = Services.Cookies.getItem(AccessTokenKey.admin);

      if (accessToken) {
        const {
          value: user,
        } = await setupAuthentication();

        if (user !== null) {
          await setupUser(user);

          return user;
        }
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }

    return null;
  }

  async setupLanguage(): Promise<void> {
    const {
      setupLanguage,
    } = this.props;

    try {
      await setupLanguage();
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async setupRegionsData(): Promise<void> {
    const {
      getRegions,
    } = this.props;

    try {
      await getRegions();
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async setupMainData(): Promise<void> {
    const {
      location,
      setupMainData,
    } = this.props;

    try {
      const {
        regatta_id: RegattaId,
        region_season_id: RegionSeasonId,
      } = getUrlGetParams(location.search);

      await setupMainData({
        RegattaId,
        RegionSeasonId,
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const models = ['Region', 'RegionSeason'];

        if (_.includes(
          models,
          data.modelName,
        )) {
          this.setupRegionsData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  async init(): Promise<void> {
    this.setupSocket();

    await this.setupLanguage();

    await this.setupRegionsData();

    const user = await this.setupAuthData();

    if (user !== null) {
      await this.setupMainData();
    }

    this.setState({
      initialised: true,
    });
  }

  render() {
    const {
      auth,
      children,
    } = this.props;
    const {
      initialised,
    } = this.state;

    return (
      <Loader
        loading={initialised === false}
      >
        <Base
          clean={auth.user === null}
          footer={(
            <Footer />
          )}
        >
          <React.Suspense
            fallback={(
              <div />
            )}
          >
            {children}
          </React.Suspense>
        </Base>
      </Loader>
    );
  }
}

export default MainView;
